import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "sonner";

const inp = "w-full bg-[#191A1D] border border-[#3A3D43] rounded-md px-3 py-3 text-sm focus:border-[#EF1B17] focus:outline-none";

function safeReturnTo(raw) {
  if (!raw) return null;
  try { const decoded = decodeURIComponent(raw); if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded; } catch {}
  return null;
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = safeReturnTo(new URLSearchParams(loc.search).get("returnTo"));
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(phone, password);
      toast.success("مرحباً بعودتك");
      if (u.role === "admin") nav("/admin");
      else nav(returnTo || "/account");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "خطأ في تسجيل الدخول");
    } finally { setLoading(false); }
  };

  const regHref = returnTo ? `/register?returnTo=${encodeURIComponent(returnTo)}` : "/register";

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top pb-16 min-h-[70vh] flex items-center">
        <div className="pg-container">
          <div className="max-w-md mx-auto bg-[#24262B] border border-[#3A3D43] rounded-lg p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl font-black mb-2">تسجيل الدخول</h1>
            <p className="text-sm text-gray-400 mb-6">أدخل رقم هاتفك وكلمة المرور</p>
            <form onSubmit={submit} className="space-y-4">
              <label className="block"><span className="text-xs font-bold text-gray-400">رقم الهاتف</span>
                <input data-testid="login-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07XXXXXXXXX" className={inp + " mt-1"} required /></label>
              <label className="block"><span className="text-xs font-bold text-gray-400">كلمة المرور</span>
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inp + " mt-1"} required /></label>
              <label className="flex items-center gap-2 text-sm text-gray-400">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> تذكرني
              </label>
              {err && <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{err}</div>}
              <button data-testid="login-submit" disabled={loading} className="w-full bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold py-3 rounded-md pg-lift">
                {loading ? "..." : "دخول"}
              </button>
            </form>
            <div className="mt-4 text-center text-sm text-gray-400">
              ليس لديك حساب؟ <Link to={regHref} className="text-[#EF1B17] font-bold">إنشاء حساب</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = safeReturnTo(new URLSearchParams(loc.search).get("returnTo"));
  const [form, setForm] = useState({ full_name: "", phone: "", password: "", confirm_password: "", agree_terms: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) return setErr("كلمتا المرور غير متطابقتين");
    setLoading(true); setErr("");
    try {
      await register(form);
      toast.success("تم إنشاء حسابك بنجاح");
      nav(returnTo || "/account");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "خطأ");
    } finally { setLoading(false); }
  };

  const loginHref = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login";

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top pb-16 min-h-[70vh] flex items-center">
        <div className="pg-container">
          <div className="max-w-md mx-auto bg-[#24262B] border border-[#3A3D43] rounded-lg p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl font-black mb-2">إنشاء حساب جديد</h1>
            <p className="text-sm text-gray-400 mb-6">انضم إلينا في دقيقة واحدة</p>
            <form onSubmit={submit} className="space-y-4">
              <label className="block"><span className="text-xs font-bold text-gray-400">الاسم الكامل</span>
                <input data-testid="reg-name" value={form.full_name} onChange={on("full_name")} className={inp + " mt-1"} required /></label>
              <label className="block"><span className="text-xs font-bold text-gray-400">رقم الهاتف</span>
                <input data-testid="reg-phone" value={form.phone} onChange={on("phone")} placeholder="07XXXXXXXXX" className={inp + " mt-1"} required /></label>
              <label className="block"><span className="text-xs font-bold text-gray-400">كلمة المرور</span>
                <input data-testid="reg-password" type="password" value={form.password} onChange={on("password")} className={inp + " mt-1"} required /></label>
              <label className="block"><span className="text-xs font-bold text-gray-400">تأكيد كلمة المرور</span>
                <input data-testid="reg-confirm" type="password" value={form.confirm_password} onChange={on("confirm_password")} className={inp + " mt-1"} required /></label>
              <label className="flex items-start gap-2 text-sm text-gray-300">
                <input data-testid="reg-terms" type="checkbox" checked={form.agree_terms} onChange={on("agree_terms")} className="mt-1" required /> أوافق على الشروط والأحكام
              </label>
              {err && <div data-testid="reg-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{err}</div>}
              <button data-testid="reg-submit" disabled={loading} className="w-full bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold py-3 rounded-md">
                {loading ? "..." : "إنشاء الحساب"}
              </button>
            </form>
            <div className="mt-4 text-center text-sm text-gray-400">
              لديك حساب بالفعل؟ <Link to={loginHref} className="text-[#EF1B17] font-bold">تسجيل الدخول</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
